/**
 * Help Center SSO redirect helpers for the Support sidebar entry.
 *
 * Opens a blank popup synchronously on click (popup-blocker survival),
 * mints an SSO JWT, then navigates the popup to Zendesk `/access/jwt`.
 *
 * Same TruSpace user → direct JWT (seamless, as before).
 * Different TruSpace user than last SSO → hop through `/access/logout`
 * first so the prior Help Center cookie does not stick.
 */

import { zendeskSsoToken } from '@/api/endpoints/support-zendesk';

const DEFAULT_ZENDESK_SUBDOMAIN = 'stevensonsystemssupport';

/**
 * Thrown when the browser returned no window handle, i.e. a pop-up blocker
 * swallowed the Help Center tab and nothing actually opened. Distinct from a
 * token-mint failure because the remedy is the user's pop-up setting, and the
 * message is what callers surface via `getApiErrorMessage`.
 */
export class ZendeskPopupBlockedError extends Error {
  constructor() {
    super('Allow pop-ups for TruSpace to open Support, then try again.');
    this.name = 'ZendeskPopupBlockedError';
  }
}

/** sessionStorage key for the TruSpace user id last SSO'd into Help Center. */
const LAST_SSO_USER_KEY = 'truspace.zendeskHc.lastSsoUserId';

/**
 * Resolve the Zendesk Help Center subdomain from env (with default).
 */
function zendeskSubdomain(): string {
  return (
    (import.meta.env.VITE_ZENDESK_SUBDOMAIN as string | undefined)?.trim() ||
    DEFAULT_ZENDESK_SUBDOMAIN
  );
}

/**
 * Absolute Help Center URL used as `return_to` after SSO.
 * No `?role=` query — role is carried in the JWT custom field (OQ-5).
 */
export function buildZendeskReturnTo(): string {
  return `https://${zendeskSubdomain()}.zendesk.com/hc/en-us`;
}

/**
 * Build the Zendesk JWT access URL for a minted SSO token.
 *
 * @param jwt - Server-signed Help Center SSO JWT.
 * @param returnTo - Absolute URL Zendesk should redirect to after auth.
 */
export function buildZendeskAccessUrl(jwt: string, returnTo: string): string {
  const url = new URL(`https://${zendeskSubdomain()}.zendesk.com/access/jwt`);
  url.searchParams.set('jwt', jwt);
  url.searchParams.set('return_to', returnTo);
  return url.toString();
}

/**
 * Build a logout→re-SSO hop URL for account switches.
 *
 * @param accessUrl - Full `/access/jwt?...` URL for the new identity.
 */
export function buildZendeskLogoutThenAccessUrl(accessUrl: string): string {
  const url = new URL(`https://${zendeskSubdomain()}.zendesk.com/access/logout`);
  url.searchParams.set('return_to', accessUrl);
  return url.toString();
}

/**
 * Read the TruSpace user id that last completed Help Center SSO in this tab.
 */
function readLastSsoUserId(): number | null {
  try {
    const raw = sessionStorage.getItem(LAST_SSO_USER_KEY);
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Persist the TruSpace user id that just completed Help Center SSO.
 */
function writeLastSsoUserId(userId: number): void {
  try {
    sessionStorage.setItem(LAST_SSO_USER_KEY, String(userId));
  } catch {
    // Ignore quota / private-mode failures.
  }
}

/**
 * Whether we should clear the prior Help Center session before SSO.
 *
 * True only when a different TruSpace user previously SSO'd in this tab.
 * First click (no prior) and same-user re-clicks stay on the seamless path.
 *
 * @param userId - Current TruSpace user id.
 */
export function shouldClearZendeskHelpCenterSession(userId: number): boolean {
  const last = readLastSsoUserId();
  return last !== null && last !== userId;
}

/**
 * True when `returnTo` is a same-subdomain Zendesk Help Center URL (DEF-417 gateway).
 */
export function isAllowedZendeskHelpCenterReturnTo(returnTo: string): boolean {
  try {
    const url = new URL(returnTo);
    const expectedHost = `${zendeskSubdomain()}.zendesk.com`;
    return url.protocol === 'https:' && url.hostname === expectedHost && url.pathname.startsWith('/hc/');
  } catch {
    return false;
  }
}

/**
 * Open Zendesk Help Center via SSO in a new tab.
 *
 * Must be called from a user gesture so `window.open('about:blank')`
 * is not blocked. On token failure the blank popup is closed and the
 * error is rethrown for the caller to toast.
 *
 * @param userId - Current TruSpace user id (used to detect account switches).
 * @param articleReturnTo - Optional absolute Zendesk HC article URL (share-email gateway).
 * @throws ZendeskPopupBlockedError when no window handle could be obtained.
 */
export async function openZendeskHelpCenterSso(
  userId: number,
  articleReturnTo?: string | null,
): Promise<void> {
  const returnTo =
    articleReturnTo && isAllowedZendeskHelpCenterReturnTo(articleReturnTo)
      ? articleReturnTo
      : buildZendeskReturnTo();
  const popup = window.open('about:blank', '_blank');

  try {
    const { jwt } = await zendeskSsoToken(returnTo);
    const accessUrl = buildZendeskAccessUrl(jwt, returnTo);
    // Same user → seamless JWT. Switched user → logout hop then JWT.
    const targetUrl = shouldClearZendeskHelpCenterSession(userId)
      ? buildZendeskLogoutThenAccessUrl(accessUrl)
      : accessUrl;

    if (popup && !popup.closed) {
      popup.location.href = targetUrl;
    } else if (!window.open(targetUrl, '_blank')) {
      // Blank popup was blocked and the direct retry got no window either, so
      // nothing opened. DEF-256: callers treat a resolved promise as "Support
      // opened" (the checklist completes its step on it), so fail instead.
      throw new ZendeskPopupBlockedError();
    }

    writeLastSsoUserId(userId);
  } catch (err) {
    popup?.close();
    throw err;
  }
}
